import { isFeatureEnabled } from "@/config/feature-flags";
import { useNetworkType } from "@/hooks/useNetworkType";
import { supabase } from "@/integrations/supabase/client";
import { getSignedVideoUrl } from "@/utils/getSignedVideoUrl";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export type Quality = "low" | "standard" | "high" | "auto";

export interface BackgroundTrack {
  id: string;
  title: string;
  artist?: string;
  duration_seconds: number;
  bpm?: number;
  cover_url?: string;
  url_low: string;
  url_std: string;
  url_high: string;
}

const ENABLE_BACKGROUND_MUSIC_DEBUG = false;
const BACKGROUND_MUSIC_URL_NOT_FOUND = "BACKGROUND_MUSIC_URL_NOT_FOUND";
const BACKGROUND_MUSIC_START_TRACK_ID_KEY = "music_start_track_id";
const BACKGROUND_MUSIC_DEFAULT_START_TITLE_HINT = "shine";
type TrackUrlKey = "url_low" | "url_std" | "url_high";

const TRACK_URL_FALLBACK_PRIORITY: Record<TrackUrlKey, TrackUrlKey[]> = {
  url_high: ["url_high", "url_std", "url_low"],
  url_std: ["url_std", "url_high", "url_low"],
  url_low: ["url_low", "url_std", "url_high"],
};

const debugBackgroundMusicLog = (...args: unknown[]): void => {
  if (!ENABLE_BACKGROUND_MUSIC_DEBUG || !import.meta.env.DEV) return;
  console.debug(...args);
};

function normalizeTrackTitle(value: string): string {
  return value.trim().toLowerCase();
}

function isDefaultStartTrackTitle(title: string): boolean {
  const normalized = normalizeTrackTitle(title);
  // Robust gegen kleinere Schreibweisen: "Shine ... Stage"
  return (
    normalized.includes(BACKGROUND_MUSIC_DEFAULT_START_TITLE_HINT) &&
    normalized.includes("stage")
  );
}

export type SpeedAdjustmentMode = "original" | "targetBpm" | "manual";

interface BackgroundMusicContextValue {
  // State
  tracks: BackgroundTrack[];
  currentTrack: BackgroundTrack | null;
  currentTrackIndex: number;
  isPlaying: boolean;
  volume: number; // 0-1
  /** Stumm (z. B. Karussell „Originalgeschwindigkeit ohne Musik“). Gain = 0, volume bleibt unverändert. */
  muted: boolean;
  quality: Quality;
  wifiOnly: boolean;
  enabled: boolean;
  isLoading: boolean;
  hasConsent: boolean; // User-Gesture für Autoplay
  autoPlayOnMobile: boolean; // Auto-Play auf mobilen Geräten
  wasManuallyTriggered: boolean; // True wenn Musik manuell gestartet wurde
  speedAdjustmentMode: SpeedAdjustmentMode; // "original" | "targetBpm" | "manual"
  targetBpm: number | null; // Ziel-BPM für Modus 2 (z.B. 106)
  shuffle: boolean; // Zufälliger Modus für Track-Wiedergabe
  /** Wiedergabe-Geschwindigkeit der Hintergrundmusik (0.25–2.0). Einheitlich für alle Tracks; Video-Sync nutzt getCurrentTime (Track-Position), daher korrekt. */
  playbackRate: number;
  /** Immer gesetzte Ziel-BPM für Video-/Sync-Logik: 106 wenn Musik aus, sonst targetBpm ?? 106. */
  effectiveTargetBpm: number;

  // Actions
  giveConsent: () => Promise<void>; // Startet ersten Track
  play: () => Promise<void>;
  playManually: () => Promise<void>; // Startet Musik manuell (für Coachmark)
  playAuto: () => Promise<void>; // Startet Musik automatisch ohne wasManuallyTriggered Flag
  pause: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  playTrack: (trackId: string) => Promise<void>;
  /** Spielt den Track ab Sekunde 0 ab (für Debug/Sequenzen, um Beat-1 sauber zu setzen). */
  playTrackFromStart: (trackId: string) => Promise<void>;
  /** Springt zu einem bestimmten Zeitpunkt im aktuellen Track (in Sekunden). */
  seekTo: (timeSeconds: number) => void;
  /** Aktuelle Audio-Zeit in Sekunden (oder 0, wenn noch kein Audio initialisiert ist). */
  getCurrentTime: () => number;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  setQuality: (quality: Quality) => void;
  setWifiOnly: (enabled: boolean) => void;
  setEnabled: (enabled: boolean) => void;
  setAutoPlayOnMobile: (enabled: boolean) => void;
  /** Setzt die Playback-Rate der Hintergrundmusik (synchron mit Video-Geschwindigkeit). */
  setPlaybackRate: (rate: number) => void;
  setSpeedAdjustmentMode: (mode: SpeedAdjustmentMode) => void;
  setTargetBpm: (bpm: number | null) => void;
  setShuffle: (enabled: boolean) => void;
  tryAutoPlay: () => Promise<void>; // Versucht Auto-Play auf mobilen Geräten
  /** Gibt das Audio-Element zurück (für Video-Export mit Audio). */
  getAudioElement: () => HTMLAudioElement | null;
}

const BackgroundMusicContext =
  createContext<BackgroundMusicContextValue | null>(null);

export function BackgroundMusicProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // State
  const [tracks, setTracks] = useState<BackgroundTrack[]>([]);
  const [currentTrack, setCurrentTrack] = useState<BackgroundTrack | null>(
    null
  );
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.7);
  const [muted, setMutedState] = useState(false);
  const [quality, setQualityState] = useState<Quality>("auto");
  const [wifiOnly, setWifiOnlyState] = useState(false);
  const [enabled, setEnabledState] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [autoPlayOnMobile, setAutoPlayOnMobileState] = useState(true);
  const [wasManuallyTriggered, setWasManuallyTriggered] = useState(false);
  const [speedAdjustmentMode, setSpeedAdjustmentModeState] =
    useState<SpeedAdjustmentMode>("targetBpm");
  const [targetBpm, setTargetBpmState] = useState<number | null>(106);
  const [shuffle, setShuffleState] = useState(true); // Standardmäßig aktiviert
  const [playbackRate, setPlaybackRateState] = useState(1.0);

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const nextTrackRef = useRef<HTMLAudioElement | null>(null);
  // Shuffle-Liste: Array von Indizes in zufälliger Reihenfolge
  const shuffledIndicesRef = useRef<number[]>([]);
  // Aktueller Index in der Shuffle-Liste
  const currentShuffleIndexRef = useRef<number>(0);
  // Lock für Track-Wechsel, um Race Conditions zu vermeiden
  const isChangingTrackRef = useRef<boolean>(false);

  /** Erst true nach explizitem Play/Karussell-Start – verhindert ~9 MB Audio-Download beim Dashboard-Mount. */
  const audioMediaWarmupAllowedRef = useRef(false);
  const [audioWarmupGeneration, setAudioWarmupGeneration] = useState(0);

  // URL Cache für vorab geladene Signed URLs (key: original URL, value: resolved URL)
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  const missingTrackUrlsRef = useRef<Set<string>>(new Set());

  // Network detection
  const networkType = useNetworkType();

  // Load settings from localStorage
  useEffect(() => {
    const savedVolume = localStorage.getItem("music_volume");
    const savedQuality = localStorage.getItem("music_quality") as Quality;
    const savedWifiOnly = localStorage.getItem("music_wifi_only");
    const savedEnabled = localStorage.getItem("music_enabled");
    const savedConsent = localStorage.getItem("music_consent");
    const savedAutoPlayOnMobile = localStorage.getItem("music_autoplay_mobile");
    const savedSpeedMode = localStorage.getItem(
      "music_speed_mode"
    ) as SpeedAdjustmentMode | null;
    const savedTargetBpm = localStorage.getItem("music_target_bpm");
    const savedShuffle = localStorage.getItem("music_shuffle");
    const savedPlaybackRate = localStorage.getItem("music_playback_rate");

    if (savedVolume) setVolumeState(parseFloat(savedVolume));
    if (savedQuality) setQualityState(savedQuality);
    if (savedWifiOnly) setWifiOnlyState(savedWifiOnly === "true");
    if (savedEnabled) setEnabledState(savedEnabled === "true");
    if (savedConsent) setHasConsent(savedConsent === "true");
    if (savedAutoPlayOnMobile !== null)
      setAutoPlayOnMobileState(savedAutoPlayOnMobile === "true");
    if (
      savedSpeedMode === "original" ||
      savedSpeedMode === "targetBpm" ||
      savedSpeedMode === "manual"
    )
      setSpeedAdjustmentModeState(savedSpeedMode);
    if (savedTargetBpm) {
      const bpm = parseFloat(savedTargetBpm);
      if (!Number.isNaN(bpm) && bpm >= 80 && bpm <= 130) setTargetBpmState(bpm);
    }
    if (savedShuffle !== null) setShuffleState(savedShuffle === "true");
    if (savedPlaybackRate) {
      const rate = parseFloat(savedPlaybackRate);
      if (!Number.isNaN(rate) && rate >= 0.25 && rate <= 2) setPlaybackRateState(rate);
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = useCallback(() => {
    localStorage.setItem("music_volume", volume.toString());
    localStorage.setItem("music_quality", quality);
    localStorage.setItem("music_wifi_only", wifiOnly.toString());
    localStorage.setItem("music_enabled", enabled.toString());
    localStorage.setItem("music_consent", hasConsent.toString());
    localStorage.setItem("music_autoplay_mobile", autoPlayOnMobile.toString());
    localStorage.setItem("music_speed_mode", speedAdjustmentMode);
    localStorage.setItem("music_shuffle", shuffle.toString());
    localStorage.setItem("music_playback_rate", playbackRate.toString());
    if (targetBpm !== null) {
      localStorage.setItem("music_target_bpm", targetBpm.toString());
    } else {
      localStorage.removeItem("music_target_bpm");
    }
  }, [
    volume,
    quality,
    wifiOnly,
    enabled,
    hasConsent,
    autoPlayOnMobile,
    speedAdjustmentMode,
    targetBpm,
    shuffle,
    playbackRate,
  ]);

  useEffect(() => {
    saveSettings();
  }, [saveSettings]);

  // Erstellt eine zufällige Shuffle-Liste der Track-Indizes
  const createShuffledIndices = useCallback((trackCount: number): number[] => {
    const indices = Array.from({ length: trackCount }, (_, i) => i);
    // Fisher-Yates Shuffle Algorithmus
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, []);

  // Initialisiert oder aktualisiert die Shuffle-Liste
  const initializeShuffle = useCallback(
    (trackCount: number, currentIndex: number) => {
      if (shuffle && trackCount > 0) {
        const shuffled = createShuffledIndices(trackCount);
        shuffledIndicesRef.current = shuffled;
        // Finde die Position des aktuellen Tracks in der Shuffle-Liste
        const shufflePos = shuffled.findIndex(idx => idx === currentIndex);
        currentShuffleIndexRef.current =
          shufflePos !== -1
            ? shufflePos
            : Math.floor(Math.random() * shuffled.length);
        debugBackgroundMusicLog(
          `🎵 [Background Music] Shuffle-Liste erstellt:`,
          {
            totalTracks: trackCount,
            currentIndex,
            shufflePosition: currentShuffleIndexRef.current,
          }
        );
      } else {
        // Wenn Shuffle deaktiviert, verwende normale Indizes
        shuffledIndicesRef.current = Array.from(
          { length: trackCount },
          (_, i) => i
        );
        currentShuffleIndexRef.current = currentIndex;
      }
    },
    [shuffle, createShuffledIndices]
  );

  // Load tracks from Supabase
  useEffect(() => {
    const loadTracks = async () => {
      setIsLoading(true);
      try {
        let trackIds: string[] | null = null;
        if (isFeatureEnabled("BACKGROUND_MUSIC_ONLY_WITH_STRUCTURED_BEATMAP")) {
          const { data: ids, error: rpcError } = await supabase.rpc(
            "get_background_track_ids_with_structured_beatmap"
          );
          if (rpcError) {
            console.warn("[Background Music] RPC Beatmap-Filter fehlgeschlagen, zeige alle Tracks", rpcError);
          } else {
            trackIds = (ids ?? []) as string[];
            if (trackIds.length === 0) {
              setTracks([]);
              setIsLoading(false);
              return;
            }
          }
        }

        // Optimierung: Lade nur benötigte Spalten, nicht beat_times (kann sehr groß sein)
        let query = supabase
          .from("background_tracks")
          .select(
            "id, title, artist, duration_seconds, bpm, cover_url, url_low, url_std, url_high, is_active, display_order"
          )
          .eq("is_active", true);

        if (trackIds !== null && trackIds.length > 0) {
          query = query.in("id", trackIds);
        }

        const { data, error } = await query.order("display_order", { ascending: true });

        if (error) throw error;

        let loadedTracks = (data || []) as BackgroundTrack[];

        // Fest vorgesehener Showcase-Start: „Shine … Stage“ (tolerant erkannt).
        // Wenn nur Tracks mit strukturierter Beatmap geladen werden, kann dieser Titel
        // aus der IN-Liste fallen – dann wäre der Default tot und ein Zufallstrack startet.
        if (
          loadedTracks.length > 0 &&
          loadedTracks.findIndex(track => isDefaultStartTrackTitle(track.title)) <
            0
        ) {
          const { data: shineRows, error: shineError } = await supabase
            .from("background_tracks")
            .select(
              "id, title, artist, duration_seconds, bpm, cover_url, url_low, url_std, url_high, is_active, display_order"
            )
            .eq("is_active", true)
            .ilike("title", "%shine%");

          if (!shineError && shineRows?.length) {
            const shineTrack = (shineRows as BackgroundTrack[]).find(track =>
              isDefaultStartTrackTitle(track.title)
            );
            if (
              shineTrack &&
              !loadedTracks.some(t => t.id === shineTrack.id)
            ) {
              loadedTracks = [shineTrack, ...loadedTracks];
            }
          }
        }

        setTracks(loadedTracks);

        // Wähle festen Default-Start-Track, sonst gespeicherten Track, sonst zufällig
        if (loadedTracks.length > 0) {
          const defaultStartIndex = loadedTracks.findIndex(track =>
            isDefaultStartTrackTitle(track.title)
          );
          const preferredTrackId = localStorage.getItem(
            BACKGROUND_MUSIC_START_TRACK_ID_KEY
          );
          const preferredIndex =
            preferredTrackId != null
              ? loadedTracks.findIndex(track => track.id === preferredTrackId)
              : -1;
          const initialIndex =
            defaultStartIndex >= 0
              ? defaultStartIndex
              : preferredIndex >= 0
                ? preferredIndex
                : Math.floor(Math.random() * loadedTracks.length);

          setCurrentTrackIndex(initialIndex);

          // Initialisiere Shuffle-Liste
          initializeShuffle(loadedTracks.length, initialIndex);

          debugBackgroundMusicLog(
            `🎵 [Background Music] Start-Track ausgewählt:`,
            {
              totalTracks: loadedTracks.length,
              selectedIndex: initialIndex,
              trackTitle: loadedTracks[initialIndex]?.title,
              usedDefaultStartTrack: defaultStartIndex >= 0,
              usedPreferredTrack: preferredIndex >= 0,
              shuffleEnabled: shuffle,
            }
          );

          // URL-/Audio-Preload erst bei enableAudioMediaWarmup() (Karussell/Play),
          // nicht beim Dashboard-Mount (~9 MB sonst sofort).
        }
      } catch (error) {
        console.error(
          "🎵 [Background Music] Error loading background tracks:",
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // preloadTrackUrls wird innerhalb der Funktion verwendet, nicht als Dependency

  // Initialize audio context and gain node
  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
    }
  }, []);

  // Transform URL to use correct Supabase instance
  const transformUrl = useCallback((url: string): string => {
    if (!url) return url;

    // Get current Supabase URL from environment
    const currentSupabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    // Replace placeholder URLs with current environment URL
    if (url.includes("your-supabase-url.supabase.co")) {
      return url.replace(
        "https://your-supabase-url.supabase.co",
        currentSupabaseUrl
      );
    }

    // Replace local development URLs with current environment URL
    if (url.includes("127.0.0.1:54321")) {
      return url.replace("http://127.0.0.1:54321", currentSupabaseUrl);
    }

    // If URL already contains the correct Supabase URL, return as is
    if (url.includes(currentSupabaseUrl)) {
      return url;
    }

    // For any other case, try to construct the URL using current environment
    const urlPath = url.split("/storage/v1/object/public/")[1];
    if (urlPath) {
      return `${currentSupabaseUrl}/storage/v1/object/public/${urlPath}`;
    }

    return url;
  }, []);

  // Validiert eine URL, indem geprüft wird, ob sie tatsächlich geladen werden kann
  const validateUrl = useCallback(async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, {
        method: "HEAD",
        signal: AbortSignal.timeout(5000),
      });
      // 200-299 = OK, 300-399 = Redirects (auch OK), 400+ = Fehler
      return response.status < 400;
    } catch {
      // Bei Netzwerkfehlern oder Timeouts nehmen wir an, dass die URL ungültig ist
      return false;
    }
  }, []);

  // Resolve final playable URL (handles local dev signed URLs for background-music)
  // Prüft zuerst den Cache, um verzögerte Edge Function Calls zu vermeiden
  const resolvePlayableUrl = useCallback(
    async (
      url: string,
      useCache = true,
      options: { failOnMissing?: boolean; validate?: boolean } = {}
    ): Promise<string> => {
      if (!url) throw new Error("Missing track URL");
      // Prüfe Cache zuerst (nur wenn useCache=true)
      if (useCache && urlCacheRef.current.has(url)) {
        const cached = urlCacheRef.current.get(url);
        if (cached) {
          // Wenn validate aktiv ist, darf der Cache keine ungültigen URLs "festhalten".
          if (options.validate) {
            const ok = await validateUrl(cached);
            if (!ok) {
              urlCacheRef.current.delete(url);
              if (options.failOnMissing) {
                missingTrackUrlsRef.current.add(url);
                throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
              }
            } else {
              return cached;
            }
          } else {
            return cached;
          }
        }
      }

      if (options.failOnMissing && missingTrackUrlsRef.current.has(url)) {
        throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
      }

      const baseUrl = transformUrl(url);
      try {
        const currentSupabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const isLocalSupabase =
          typeof currentSupabaseUrl === "string" &&
          currentSupabaseUrl.includes("127.0.0.1:54321");

        // In lokaler Umgebung liefern wir für background-music lieber eine signierte URL,
        // da öffentliche URLs bei Range-Requests für Audio Probleme machen können.
        if (
          isLocalSupabase &&
          baseUrl.includes("/storage/v1/object/public/background-music/")
        ) {
          const afterPublic = baseUrl.split(
            "/storage/v1/object/public/background-music/"
          )[1];
          if (afterPublic) {
            const signed = await getSignedVideoUrl(afterPublic, {
              bucket: "background-music",
              ttlSeconds: 3600,
              suppressErrorLog: true,
            });
            const resolvedUrl = signed || baseUrl;

            // Validiere die URL, wenn gewünscht
            if (options.validate && resolvedUrl) {
              const isValid = await validateUrl(resolvedUrl);
              if (!isValid) {
                if (options.failOnMissing) {
                  missingTrackUrlsRef.current.add(url);
                  throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
                }
                // Fallback auf baseUrl, aber auch validieren
                const baseIsValid = await validateUrl(baseUrl);
                if (!baseIsValid && options.failOnMissing) {
                  missingTrackUrlsRef.current.add(url);
                  throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
                }
                // Cache die baseUrl, wenn sie gültig ist
                if (useCache && baseIsValid) {
                  urlCacheRef.current.set(url, baseUrl);
                }
                return baseIsValid ? baseUrl : resolvedUrl; // Trotzdem zurückgeben, auch wenn ungültig
              }
            }

            // Cache die resolved URL für zukünftige Verwendung
            if (useCache) {
              urlCacheRef.current.set(url, resolvedUrl);
            }
            return resolvedUrl;
          }
        }
      } catch (err) {
        const message =
          (err as { message?: string })?.message ?? "Unknown error";
        const isMissing =
          message.includes("Object not found") ||
          message.includes("Storage error") ||
          message === BACKGROUND_MUSIC_URL_NOT_FOUND;
        if (options.failOnMissing && isMissing) {
          missingTrackUrlsRef.current.add(url);
          debugBackgroundMusicLog(
            "🎵 [Background Music] Markiere fehlende Track-URL",
            url
          );
          throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
        }
        debugBackgroundMusicLog(
          "🎵 [Background Music] Fallback auf public URL (Signierung fehlgeschlagen)",
          err
        );
      }

      // Validiere die baseUrl, wenn gewünscht
      if (options.validate) {
        const isValid = await validateUrl(baseUrl);
        if (!isValid && options.failOnMissing) {
          missingTrackUrlsRef.current.add(url);
          throw new Error(BACKGROUND_MUSIC_URL_NOT_FOUND);
        }
        // Wenn ungültig und failOnMissing=false, trotzdem zurückgeben (für Fallback-Logik)
      }

      // Cache auch die public URL (falls keine Signierung nötig)
      if (useCache) {
        urlCacheRef.current.set(url, baseUrl);
      }
      return baseUrl;
    },
    [transformUrl, validateUrl]
  );

  const determineEffectiveQuality = useCallback((): Exclude<
    Quality,
    "auto"
  > => {
    if (quality === "auto") {
      if (wifiOnly && networkType === "cellular") {
        return "low";
      }
      switch (networkType) {
        case "wifi":
          return "high";
        case "cellular":
          return "low";
        default:
          return "standard";
      }
    }
    return quality;
  }, [quality, wifiOnly, networkType]);

  const qualityToUrlKey = (value: Exclude<Quality, "auto">): TrackUrlKey => {
    switch (value) {
      case "high":
        return "url_high";
      case "low":
        return "url_low";
      default:
        return "url_std";
    }
  };

  const resolveTrackUrlWithFallback = useCallback(
    async (
      track: BackgroundTrack,
      preferredKey: TrackUrlKey,
      useCache = true,
      validate = false
    ): Promise<string> => {
      const fallbackOrder = TRACK_URL_FALLBACK_PRIORITY[preferredKey];
      const currentSupabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const isLocalSupabase =
        typeof currentSupabaseUrl === "string" &&
        currentSupabaseUrl.includes("127.0.0.1:54321");

      // Sammle alle fehlgeschlagenen URLs für bessere Fehlermeldung
      const failedUrls: Array<{ key: TrackUrlKey; url: string }> = [];

      for (const key of fallbackOrder) {
        const candidate = track[key];
        if (!candidate) continue;
        try {
          return await resolvePlayableUrl(candidate, useCache, {
            failOnMissing: true,
            validate,
          });
        } catch (error) {
          if (
            error instanceof Error &&
            error.message === BACKGROUND_MUSIC_URL_NOT_FOUND
          ) {
            debugBackgroundMusicLog(
              "🎵 [Background Music] Qualität fehlt, versuche Fallback",
              { trackId: track.id, missingQuality: key }
            );
            failedUrls.push({ key, url: candidate });
            continue;
          }
          throw error;
        }
      }

      // Wenn alle Qualitäten fehlen:
      // In lokaler Umgebung: Versuche trotzdem die erste URL ohne Validierung
      // (kann sein, dass die Dateien existieren, aber die Validierung fehlschlägt)
      if (isLocalSupabase && failedUrls.length > 0) {
        const firstFailed = failedUrls[0];
        debugBackgroundMusicLog(
          "🎵 [Background Music] Alle Qualitäten fehlgeschlagen, versuche lokalen Fallback ohne Validierung",
          { trackId: track.id, url: firstFailed.url }
        );
        try {
          // Versuche ohne Validierung (kann sein, dass die Datei existiert, aber HEAD fehlschlägt)
          return await resolvePlayableUrl(firstFailed.url, useCache, {
            failOnMissing: false,
            validate: false,
          });
        } catch {
          // Auch das hat nicht funktioniert - wirf den ursprünglichen Fehler
        }
      }

      // Fehlermeldung mit Details
      const missingQualities = failedUrls.map(f => f.key).join(", ");
      throw new Error(
        `No playable background track URL available for ${track.title}. Missing qualities: ${missingQualities}`
      );
    },
    [resolvePlayableUrl]
  );

  // Get quality URL based on settings and network
  // Verwendet Cache für sofortige Antwort (kein Edge Function Call beim Play-Klick)
  const getQualityUrl = useCallback(
    async (
      track: BackgroundTrack,
      useCache = true,
      validate = false
    ): Promise<string> => {
      const effectiveQuality = determineEffectiveQuality();
      const preferredKey = qualityToUrlKey(effectiveQuality);
      return resolveTrackUrlWithFallback(
        track,
        preferredKey,
        useCache,
        validate
      );
    },
    [determineEffectiveQuality, resolveTrackUrlWithFallback]
  );

  // Preload URLs für einen Track (nur wahrscheinlichste Qualität) - läuft im Hintergrund
  // WICHTIG: Muss VOR dem useEffect definiert werden, das es verwendet
  const preloadTrackUrls = useCallback(
    async (track: BackgroundTrack) => {
      const effectiveQuality = determineEffectiveQuality();
      const preferredKey = qualityToUrlKey(effectiveQuality);
      try {
        await resolveTrackUrlWithFallback(track, preferredKey, true);
      } catch {
        // Fehler beim Preload sind ok - wird beim tatsächlichen Abspielen nochmal versucht
      }
    },
    [determineEffectiveQuality, resolveTrackUrlWithFallback]
  );

  const enableAudioMediaWarmup = useCallback(() => {
    if (audioMediaWarmupAllowedRef.current) return;
    audioMediaWarmupAllowedRef.current = true;
    if (audioRef.current) {
      audioRef.current.preload = "auto";
    }
    setAudioWarmupGeneration(n => n + 1);
    if (tracks.length === 0) return;
    const current = tracks[currentTrackIndex];
    if (current) {
      void preloadTrackUrls(current);
    }
  }, [tracks, currentTrackIndex, preloadTrackUrls]);

  // Actions - moved before setupMediaSession to avoid initialization order issues
  const play = useCallback(async () => {
    enableAudioMediaWarmup();
    if (!audioRef.current || !currentTrack) return;

    try {
      // Resume audio context if suspended
      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      // 🚀 OPTIMIERUNG: Prüfe ob URL bereits gesetzt ist (durch Preload)
      const isLocalSupabase =
        typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
        import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
      const qualityUrl = await getQualityUrl(
        currentTrack,
        true,
        isLocalSupabase
      );

      // Nur setzen wenn noch nicht gesetzt oder anders (vermeidet unnötige Reloads)
      if (!audioRef.current.src || audioRef.current.src !== qualityUrl) {
        debugBackgroundMusicLog("🎵 [Background Music] Setting audio source:", {
          track: currentTrack.title,
          url: qualityUrl,
        });
        // Setze src auf leeren String, bevor wir eine neue URL setzen (verhindert "NotSupportedError")
        if (audioRef.current.src) {
          audioRef.current.src = "";
          audioRef.current.load();
          // Kurze Pause, damit das Reset abgeschlossen ist
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        audioRef.current.src = qualityUrl;
      } else {
        // URL ist bereits gesetzt (durch Preload) - Browser hat schon gepuffert!
        debugBackgroundMusicLog(
          "🎵 [Background Music] Audio bereits vorbereitet (Preload), starte sofort:",
          {
            track: currentTrack.title,
          }
        );
      }

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      const err = error as unknown;
      const isAbortError =
        typeof DOMException !== "undefined" &&
        err instanceof DOMException &&
        err.name === "AbortError";

      if (isAbortError) {
        debugBackgroundMusicLog(
          "🎵 [Background Music] play() wurde durch neuen Ladevorgang abgebrochen (AbortError)",
          err
        );
        // Kein setIsPlaying(false), da evtl. bereits ein neuer Track läuft
        return;
      }

      // Setze Audio-Element zurück, um "NotSupportedError" zu vermeiden
      if (audioRef.current) {
        audioRef.current.src = "";
        audioRef.current.load();
      }
      console.error("Error playing audio:", error);
      setIsPlaying(false);
    }
  }, [currentTrack, getQualityUrl, enableAudioMediaWarmup]);

  const playManually = useCallback(async () => {
    enableAudioMediaWarmup();
    // Setze Flag für manuelle Aktivierung
    setWasManuallyTriggered(true);

    // OPTIMIERUNG: Verwende den aktuellen Track (bereits gepreloadet) für sofortigen Start
    // Nur wenn kein aktueller Track vorhanden ist, wähle einen neuen zufälligen Track
    if (!isPlaying && tracks.length > 0) {
      let selectedTrack = currentTrack;
      let selectedIndex = currentTrackIndex;

      // Wenn kein aktueller Track vorhanden, wähle einen zufälligen
      if (!selectedTrack) {
        selectedIndex = Math.floor(Math.random() * tracks.length);
        selectedTrack = tracks[selectedIndex];

        debugBackgroundMusicLog(
          `🎵 [Background Music] Neuer zufälliger Track beim manuellen Start:`,
          {
            totalTracks: tracks.length,
            selectedIndex,
            trackTitle: selectedTrack?.title,
          }
        );

        // Setze Track-Index und Track direkt
        setCurrentTrackIndex(selectedIndex);
        setCurrentTrack(selectedTrack);
      } else {
        // Verwende aktuellen Track (bereits gepreloadet) für sofortigen Start
        debugBackgroundMusicLog(
          `🎵 [Background Music] Verwende aktuellen Track (bereits gepreloadet):`,
          {
            trackTitle: selectedTrack.title,
            index: selectedIndex,
          }
        );
      }

      // Spiele den ausgewählten Track direkt ab
      if (audioRef.current && selectedTrack) {
        try {
          // Resume audio context if suspended
          if (audioContextRef.current?.state === "suspended") {
            await audioContextRef.current.resume();
          }

          // 🚀 OPTIMIERUNG: Prüfe ob URL bereits gesetzt ist (durch Preload)
          const isLocalSupabase =
            typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
            import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
          const qualityUrl = await getQualityUrl(
            selectedTrack,
            true,
            isLocalSupabase
          );

          // Nur setzen wenn noch nicht gesetzt oder anders
          if (!audioRef.current.src || audioRef.current.src !== qualityUrl) {
            debugBackgroundMusicLog(
              "🎵 [Background Music] Setting audio source for track:",
              {
                track: selectedTrack.title,
                url: qualityUrl,
              }
            );
            audioRef.current.src = qualityUrl;
          } else {
            // URL ist bereits gesetzt (durch Preload) - Browser hat schon gepuffert!
            debugBackgroundMusicLog(
              "🎵 [Background Music] Audio bereits vorbereitet (Preload), starte sofort:",
              {
                track: selectedTrack.title,
              }
            );
          }

          await audioRef.current.play();
          setIsPlaying(true);

          // Setup Media Session (wird später definiert, aber hier direkt aufgerufen)
          if ("mediaSession" in navigator && selectedTrack) {
            navigator.mediaSession.metadata = new MediaMetadata({
              title: selectedTrack.title,
              artist: selectedTrack.artist || "Hustle Dance",
              artwork: selectedTrack.cover_url
                ? [
                    {
                      src: transformUrl(selectedTrack.cover_url),
                      sizes: "500x500",
                      type: "image/jpeg",
                    },
                  ]
                : [],
            });
          }

          return;
        } catch (error) {
          const err = error as unknown;
          const isAbortError =
            typeof DOMException !== "undefined" &&
            err instanceof DOMException &&
            err.name === "AbortError";

          if (isAbortError) {
            debugBackgroundMusicLog(
              "🎵 [Background Music] playManually() wurde durch neuen Ladevorgang abgebrochen (AbortError)",
              err
            );
            // Kein setIsPlaying(false), da evtl. bereits ein neuer Track läuft
            return;
          }

          console.error("Error playing audio:", error);
          setIsPlaying(false);
          return;
        }
      }
    }

    // Falls Musik bereits läuft oder kein Track ausgewählt, verwende normale play()
    await play();
  }, [play, isPlaying, tracks, getQualityUrl, transformUrl]);

  // Startet Musik automatisch ohne wasManuallyTriggered Flag zu setzen
  // Verwendet die gleiche Logik wie playManually(), aber ohne Flag
  const playAuto = useCallback(async () => {
    enableAudioMediaWarmup();
    // KEIN setWasManuallyTriggered(true) - damit keine "Nice! Viel Spaß!" Nachricht erscheint

    // OPTIMIERUNG: Verwende den aktuellen Track (bereits gepreloadet) für sofortigen Start
    // Nur wenn kein aktueller Track vorhanden ist, wähle einen neuen zufälligen Track
    if (!isPlaying && tracks.length > 0) {
      let selectedTrack = currentTrack;
      let selectedIndex = currentTrackIndex;

      // Wenn kein aktueller Track vorhanden, wähle einen zufälligen
      if (!selectedTrack) {
        selectedIndex = Math.floor(Math.random() * tracks.length);
        selectedTrack = tracks[selectedIndex];

        debugBackgroundMusicLog(
          `🎵 [Background Music] Neuer zufälliger Track beim automatischen Start:`,
          {
            totalTracks: tracks.length,
            selectedIndex,
            trackTitle: selectedTrack?.title,
          }
        );

        // Setze Track-Index und Track direkt
        setCurrentTrackIndex(selectedIndex);
        setCurrentTrack(selectedTrack);
      } else {
        // Verwende aktuellen Track (bereits gepreloadet) für sofortigen Start
        debugBackgroundMusicLog(
          `🎵 [Background Music] Verwende aktuellen Track (bereits gepreloadet):`,
          {
            trackTitle: selectedTrack.title,
            index: selectedIndex,
          }
        );
      }

      // Spiele den ausgewählten Track direkt ab
      if (audioRef.current && selectedTrack) {
        try {
          // Resume audio context if suspended
          if (audioContextRef.current?.state === "suspended") {
            await audioContextRef.current.resume();
          }

          // 🚀 OPTIMIERUNG: Prüfe ob URL bereits gesetzt ist (durch Preload)
          const qualityUrl = await getQualityUrl(selectedTrack, true);

          // Nur setzen wenn noch nicht gesetzt oder anders
          if (!audioRef.current.src || audioRef.current.src !== qualityUrl) {
            debugBackgroundMusicLog(
              "🎵 [Background Music] Setting audio source for track:",
              {
                track: selectedTrack.title,
                url: qualityUrl,
              }
            );
            audioRef.current.src = qualityUrl;
          } else {
            // URL ist bereits gesetzt (durch Preload) - Browser hat schon gepuffert!
            debugBackgroundMusicLog(
              "🎵 [Background Music] Audio bereits vorbereitet (Preload), starte sofort:",
              {
                track: selectedTrack.title,
              }
            );
          }

          await audioRef.current.play();
          setIsPlaying(true);

          // Setup Media Session (wird später definiert, aber hier direkt aufgerufen)
          if ("mediaSession" in navigator && selectedTrack) {
            navigator.mediaSession.metadata = new MediaMetadata({
              title: selectedTrack.title,
              artist: selectedTrack.artist || "Hustle Dance",
              artwork: selectedTrack.cover_url
                ? [
                    {
                      src: transformUrl(selectedTrack.cover_url),
                      sizes: "500x500",
                      type: "image/jpeg",
                    },
                  ]
                : [],
            });
          }

          return;
        } catch (error) {
          const err = error as unknown;
          const isAbortError =
            typeof DOMException !== "undefined" &&
            err instanceof DOMException &&
            err.name === "AbortError";

          if (isAbortError) {
            debugBackgroundMusicLog(
              "🎵 [Background Music] playAuto() wurde durch neuen Ladevorgang abgebrochen (AbortError)",
              err
            );
            // Kein setIsPlaying(false), da evtl. bereits ein neuer Track läuft
            return;
          }

          console.error("Error playing audio:", error);
          setIsPlaying(false);
          return;
        }
      }
    }

    // Falls Musik bereits läuft oder kein Track ausgewählt, verwende normale play()
    await play();
  }, [
    play,
    isPlaying,
    tracks,
    currentTrack,
    currentTrackIndex,
    getQualityUrl,
    transformUrl,
  ]);

  const playTrackManually = useCallback(
    async (track: BackgroundTrack) => {
      // Setze Flag für manuelle Aktivierung
      setWasManuallyTriggered(true);

      if (!audioRef.current) return;

      try {
        // Resume audio context if suspended
        if (audioContextRef.current?.state === "suspended") {
          await audioContextRef.current.resume();
        }

        // Set source for specific track
        const isLocalSupabase =
          typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
          import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
        const qualityUrl = await getQualityUrl(track, true, isLocalSupabase);
        debugBackgroundMusicLog(
          "🎵 [Background Music] Setting audio source for specific track:",
          {
            track: track.title,
            url: qualityUrl,
          }
        );
        audioRef.current.src = qualityUrl;

        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Error playing audio:", error);
        setIsPlaying(false);
      }
    },
    [getQualityUrl]
  );

  const giveConsent = useCallback(async () => {
    enableAudioMediaWarmup();
    setHasConsent(true);

    // Resume audio context if suspended
    if (audioContextRef.current?.state === "suspended") {
      await audioContextRef.current.resume();
    }

    // Start track if available (verwende bereits zufällig ausgewählten Track)
    if (tracks.length > 0) {
      await play();
    }
  }, [tracks, play, enableAudioMediaWarmup]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      // Reset manueller Trigger-Flag beim Pausieren
      setWasManuallyTriggered(false);
    }
  }, []);

  const nextTrack = useCallback(async () => {
    if (tracks.length === 0) return;

    // Verhindere Race Conditions: Wenn bereits ein Wechsel läuft, ignoriere diesen Call
    if (isChangingTrackRef.current) {
      debugBackgroundMusicLog(
        "🎵 [Background Music] Track-Wechsel bereits in Bearbeitung, ignoriere Call"
      );
      return;
    }

    // Setze Lock
    isChangingTrackRef.current = true;

    try {
      debugBackgroundMusicLog("🎵 [Background Music] Track-Wechsel Debug:", {
        currentIndex: currentTrackIndex,
        totalTracks: tracks.length,
        currentTrack: currentTrack?.title,
        shuffleEnabled: shuffle,
      });

      if (tracks.length === 1) {
        // Nur ein Track: Restart des aktuellen Tracks
        debugBackgroundMusicLog(
          "🎵 [Background Music] Only one track - restarting current track"
        );
        if (audioRef.current) {
          audioRef.current.currentTime = 0; // Zurück zum Anfang
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } else {
        // Mehrere Tracks: Wechsel basierend auf Shuffle-Modus
        let newIndex: number;

        if (shuffle && shuffledIndicesRef.current.length > 0) {
          // Shuffle-Modus: Nächster Track aus Shuffle-Liste
          currentShuffleIndexRef.current =
            (currentShuffleIndexRef.current + 1) %
            shuffledIndicesRef.current.length;
          newIndex = shuffledIndicesRef.current[currentShuffleIndexRef.current];

          // Wenn wir am Ende der Shuffle-Liste sind, erstelle eine neue Shuffle-Liste
          if (currentShuffleIndexRef.current === 0) {
            shuffledIndicesRef.current = createShuffledIndices(tracks.length);
            debugBackgroundMusicLog(
              "🎵 [Background Music] Neue Shuffle-Liste erstellt (Ende erreicht)"
            );
          }
        } else {
          // Normaler Modus: Sequenzieller Wechsel
          newIndex = (currentTrackIndex + 1) % tracks.length;
        }

        const nextTrack = tracks[newIndex];

        debugBackgroundMusicLog(
          "🎵 [Background Music] Switching to next track:",
          {
            newIndex,
            nextTrack: nextTrack?.title,
            shuffleEnabled: shuffle,
          }
        );

        setCurrentTrackIndex(newIndex);

        // Spiele den neuen Track direkt ab
        if (nextTrack && audioRef.current) {
          try {
            // Resume audio context if suspended
            if (audioContextRef.current?.state === "suspended") {
              await audioContextRef.current.resume();
            }

            // Set source for next track
            const isLocalSupabase =
              typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
              import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
            const qualityUrl = await getQualityUrl(
              nextTrack,
              true,
              isLocalSupabase
            );
            debugBackgroundMusicLog(
              "🎵 [Background Music] Setting audio source for next track:",
              {
                track: nextTrack.title,
                url: qualityUrl,
              }
            );
            audioRef.current.src = qualityUrl;

            await audioRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            const err = error as unknown;
            const isAbortError =
              typeof DOMException !== "undefined" &&
              err instanceof DOMException &&
              err.name === "AbortError";

            if (isAbortError) {
              // AbortError ist erwartet, wenn ein neuer Track-Wechsel den alten abbricht
              debugBackgroundMusicLog(
                "🎵 [Background Music] Track-Wechsel wurde abgebrochen (erwartet bei schnellen Wechseln)"
              );
            } else {
              console.error(
                "🎵 [Background Music] Error playing next track:",
                error
              );
              setIsPlaying(false);
            }
          }
        }
      }
    } finally {
      // Entferne Lock
      isChangingTrackRef.current = false;
    }
  }, [
    tracks.length,
    currentTrackIndex,
    tracks,
    getQualityUrl,
    shuffle,
    createShuffledIndices,
  ]);

  const previousTrack = useCallback(async () => {
    if (tracks.length === 0) return;

    // Verhindere Race Conditions: Wenn bereits ein Wechsel läuft, ignoriere diesen Call
    if (isChangingTrackRef.current) {
      debugBackgroundMusicLog(
        "🎵 [Background Music] Track-Wechsel bereits in Bearbeitung, ignoriere Call"
      );
      return;
    }

    // Setze Lock
    isChangingTrackRef.current = true;

    try {
      debugBackgroundMusicLog("🎵 [Background Music] Track-Wechsel Debug:", {
        currentIndex: currentTrackIndex,
        totalTracks: tracks.length,
        currentTrack: currentTrack?.title,
        shuffleEnabled: shuffle,
      });

      if (tracks.length === 1) {
        // Nur ein Track: Restart des aktuellen Tracks
        debugBackgroundMusicLog(
          "🎵 [Background Music] Only one track - restarting current track"
        );
        if (audioRef.current) {
          audioRef.current.currentTime = 0; // Zurück zum Anfang
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } else {
        // Mehrere Tracks: Wechsel basierend auf Shuffle-Modus
        let newIndex: number;

        if (shuffle && shuffledIndicesRef.current.length > 0) {
          // Shuffle-Modus: Vorheriger Track aus Shuffle-Liste
          currentShuffleIndexRef.current =
            (currentShuffleIndexRef.current -
              1 +
              shuffledIndicesRef.current.length) %
            shuffledIndicesRef.current.length;
          newIndex = shuffledIndicesRef.current[currentShuffleIndexRef.current];
        } else {
          // Normaler Modus: Sequenzieller Wechsel
          newIndex = (currentTrackIndex - 1 + tracks.length) % tracks.length;
        }

        const previousTrack = tracks[newIndex];

        debugBackgroundMusicLog(
          "🎵 [Background Music] Switching to previous track:",
          {
            newIndex,
            previousTrack: previousTrack?.title,
            shuffleEnabled: shuffle,
          }
        );

        setCurrentTrackIndex(newIndex);

        // Spiele den neuen Track direkt ab
        if (previousTrack && audioRef.current) {
          try {
            // Resume audio context if suspended
            if (audioContextRef.current?.state === "suspended") {
              await audioContextRef.current.resume();
            }

            // Set source for previous track
            const isLocalSupabase =
              typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
              import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
            const qualityUrl = await getQualityUrl(
              previousTrack,
              true,
              isLocalSupabase
            );
            debugBackgroundMusicLog(
              "🎵 [Background Music] Setting audio source for previous track:",
              {
                track: previousTrack.title,
                url: qualityUrl,
              }
            );
            audioRef.current.src = qualityUrl;

            await audioRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            const err = error as unknown;
            const isAbortError =
              typeof DOMException !== "undefined" &&
              err instanceof DOMException &&
              err.name === "AbortError";

            if (isAbortError) {
              // AbortError ist erwartet, wenn ein neuer Track-Wechsel den alten abbricht
              debugBackgroundMusicLog(
                "🎵 [Background Music] Track-Wechsel wurde abgebrochen (erwartet bei schnellen Wechseln)"
              );
            } else {
              console.error(
                "🎵 [Background Music] Error playing previous track:",
                error
              );
              setIsPlaying(false);
            }
          }
        }
      }
    } finally {
      // Entferne Lock
      isChangingTrackRef.current = false;
    }
  }, [tracks.length, currentTrackIndex, tracks, getQualityUrl, shuffle]);

  const playTrack = useCallback(
    async (trackId: string) => {
      const index = tracks.findIndex(track => track.id === trackId);
      if (index !== -1) {
        setCurrentTrackIndex(index);
        await play();
      }
    },
    [tracks, play]
  );

  const playTrackFromStart = useCallback(
    async (trackId: string) => {
      enableAudioMediaWarmup();
      const index = tracks.findIndex(track => track.id === trackId);
      if (index === -1) return;

      const track = tracks[index];
      setCurrentTrackIndex(index);
      setCurrentTrack(track);

      if (!audioRef.current) {
        // Fallback: falls Audio-Element noch nicht bereit ist, nutze Standard-Play
        await play();
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
        return;
      }

      try {
        if (audioContextRef.current?.state === "suspended") {
          await audioContextRef.current.resume();
        }

        // Prüfe, ob der Track bereits geladen ist
        // Validiere URLs nicht standardmäßig (Performance), aber setze Audio-Element zurück bei Fehlern
        const isLocalSupabase =
          typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
          import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
        const qualityUrl = await getQualityUrl(track, true, isLocalSupabase);
        const needsReload = audioRef.current.src !== qualityUrl;

        if (needsReload) {
          // Nur neu laden, wenn es ein anderer Track ist
          // Setze src auf leeren String, bevor wir eine neue URL setzen (verhindert "NotSupportedError")
          audioRef.current.src = "";
          audioRef.current.load(); // Reset das Audio-Element

          // Kurze Pause, damit das Reset abgeschlossen ist
          await new Promise(resolve => setTimeout(resolve, 50));

          audioRef.current.src = qualityUrl;

          // Prüfe, ob das Audio bereits geladen ist (readyState >= 2 = HAVE_CURRENT_DATA)
          if (audioRef.current.readyState >= 2) {
            // Audio ist bereits geladen, kein Warten nötig
          } else {
            // Warte auf loadedmetadata, bevor wir play() aufrufen
            await new Promise<void>((resolve, reject) => {
              if (!audioRef.current) {
                reject(new Error("Audio element not available"));
                return;
              }

              // Prüfe nochmal, ob es inzwischen geladen wurde
              if (audioRef.current.readyState >= 2) {
                resolve();
                return;
              }

              const timeoutId: { value: number | undefined } = {
                value: undefined,
              };
              const cleanup = () => {
                if (timeoutId.value) clearTimeout(timeoutId.value);
                audioRef.current?.removeEventListener(
                  "loadedmetadata",
                  onLoadedMetadata
                );
                audioRef.current?.removeEventListener("error", onError);
                audioRef.current?.removeEventListener("canplay", onCanPlay);
              };

              const onLoadedMetadata = () => {
                cleanup();
                resolve();
              };
              const onCanPlay = () => {
                cleanup();
                resolve();
              };
              const onError = (event: Event) => {
                cleanup();
                const error = audioRef.current?.error;
                const errorMessage = error
                  ? `Failed to load audio: ${error.code} (${error.message})`
                  : "Failed to load audio";
                // Setze Audio-Element zurück, um "NotSupportedError" zu vermeiden
                if (audioRef.current) {
                  audioRef.current.src = "";
                  audioRef.current.load();
                }
                reject(new Error(errorMessage));
              };

              audioRef.current.addEventListener(
                "loadedmetadata",
                onLoadedMetadata
              );
              audioRef.current.addEventListener("canplay", onCanPlay);
              audioRef.current.addEventListener("error", onError);

              // Timeout nach 10 Sekunden (länger für langsame Verbindungen)
              timeoutId.value = window.setTimeout(() => {
                cleanup();
                // Setze Audio-Element zurück
                if (audioRef.current) {
                  audioRef.current.src = "";
                  audioRef.current.load();
                }
                reject(new Error("Timeout loading audio"));
              }, 10000);
            });
          }
        }

        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        // Ignoriere AbortError (passiert, wenn ein neuer Load-Request den alten abbricht)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        // Setze Audio-Element zurück, um "NotSupportedError" zu vermeiden
        if (audioRef.current) {
          audioRef.current.src = "";
          audioRef.current.load();
        }

        console.error(
          "🎵 [Background Music] Error playing track from start:",
          error
        );
        setIsPlaying(false);
      }
    },
    [tracks, getQualityUrl, play]
  );

  const getCurrentTime = useCallback((): number => {
    return audioRef.current?.currentTime ?? 0;
  }, []);

  const seekTo = useCallback((timeSeconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, timeSeconds);
    }
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m);
  }, []);

  const setQuality = useCallback((newQuality: Quality) => {
    setQualityState(newQuality);
  }, []);

  const setWifiOnly = useCallback((enabled: boolean) => {
    setWifiOnlyState(enabled);
  }, []);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setEnabledState(enabled);
      if (!enabled) {
        pause();
      }
    },
    [pause]
  );

  const setAutoPlayOnMobile = useCallback((enabled: boolean) => {
    setAutoPlayOnMobileState(enabled);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    const clampedRate = Math.max(0.25, Math.min(2.0, rate));
    setPlaybackRateState(clampedRate);
    setSpeedAdjustmentModeState("manual"); // Nutzer-Geschwindigkeit bleibt bis zur Änderung erhalten
    if (audioRef.current) {
      audioRef.current.playbackRate = clampedRate;
      debugBackgroundMusicLog(
        `🎵 [Background Music] Playback-Rate gesetzt: ${clampedRate.toFixed(2)}x`
      );
    }
  }, []);

  const setSpeedAdjustmentMode = useCallback((mode: SpeedAdjustmentMode) => {
    setSpeedAdjustmentModeState(mode);
  }, []);

  const setTargetBpm = useCallback((bpm: number | null) => {
    setTargetBpmState(bpm);
  }, []);

  const setShuffle = useCallback(
    (enabled: boolean) => {
      setShuffleState(enabled);
      // Aktualisiere Shuffle-Liste wenn sich der Modus ändert
      if (tracks.length > 0) {
        initializeShuffle(tracks.length, currentTrackIndex);
      }
    },
    [tracks.length, currentTrackIndex, initializeShuffle]
  );

  // Berechnet und setzt die Playback-Rate basierend auf Modus und Ziel-BPM (Vereinheitlichung vor Video-Sync).
  // Bei "manual" bleibt die vom Nutzer gewählte Geschwindigkeit unverändert.
  const applySpeedAdjustment = useCallback(() => {
    if (speedAdjustmentMode === "manual") {
      // Nur Audio-Element synchronisieren, Rate nicht überschreiben
      if (audioRef.current) audioRef.current.playbackRate = playbackRate;
      return;
    }
    if (speedAdjustmentMode === "original") {
      setPlaybackRateState(1.0);
      if (audioRef.current) audioRef.current.playbackRate = 1.0;
      return;
    }

    // Modus targetBpm (Standard 106 BPM wenn kein Wert gesetzt)
    const effectiveBpm = targetBpm ?? 106;
    if (!currentTrack?.bpm || currentTrack.bpm <= 0) {
      setPlaybackRateState(1.0);
      if (audioRef.current) audioRef.current.playbackRate = 1.0;
      return;
    }

    const calculatedRate = effectiveBpm / currentTrack.bpm;
    const clampedRate = Math.max(0.25, Math.min(2.0, calculatedRate));
    setPlaybackRateState(clampedRate);
    if (audioRef.current) audioRef.current.playbackRate = clampedRate;
    debugBackgroundMusicLog(
      `🎵 [Background Music] Geschwindigkeit angepasst: ${currentTrack.bpm} BPM → ${effectiveBpm} BPM (Rate: ${clampedRate.toFixed(2)}x)`
    );
  }, [speedAdjustmentMode, targetBpm, currentTrack, playbackRate]);

  const tryAutoPlay = useCallback(async () => {
    // Nur versuchen wenn Auto-Play aktiviert ist und noch kein Consent gegeben wurde
    if (!autoPlayOnMobile || hasConsent || !enabled || !currentTrack) {
      return;
    }

    // Prüfe ob wir auf einem mobilen Gerät sind
    const isMobile =
      window.innerWidth < 1024 ||
      /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent.toLowerCase()
      );

    if (isMobile) {
      try {
        debugBackgroundMusicLog(
          "🎵 [Background Music] Versuche Auto-Play auf mobilem Gerät"
        );
        await giveConsent();
      } catch (error) {
        debugBackgroundMusicLog(
          "🎵 [Background Music] Auto-Play fehlgeschlagen:",
          error
        );
        // Auto-Play fehlgeschlagen ist normal - Browser blockiert es oft
      }
    }
  }, [autoPlayOnMobile, hasConsent, enabled, currentTrack, giveConsent]);

  // Setup Media Session API
  const setupMediaSession = useCallback(
    (track: BackgroundTrack) => {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: track.title,
          artist: track.artist || "Hustle Dance",
          artwork: track.cover_url
            ? [
                {
                  src: transformUrl(track.cover_url),
                  sizes: "500x500",
                  type: "image/jpeg",
                },
              ]
            : [],
        });

        navigator.mediaSession.setActionHandler("play", () => play());
        navigator.mediaSession.setActionHandler("pause", () => pause());
        navigator.mediaSession.setActionHandler("previoustrack", () =>
          previousTrack()
        );
        navigator.mediaSession.setActionHandler("nexttrack", () => nextTrack());
      }
    },
    [play, pause, previousTrack, nextTrack, transformUrl]
  );

  // Preload next track (Audio-Element + URLs im Cache)
  const preloadNextTrack = useCallback(() => {
    if (!audioMediaWarmupAllowedRef.current) return;
    if (tracks.length === 0) return;

    // Berechne nächsten Index basierend auf Shuffle-Modus
    let nextIndex: number;
    if (shuffle && shuffledIndicesRef.current.length > 0) {
      const nextShufflePos =
        (currentShuffleIndexRef.current + 1) %
        shuffledIndicesRef.current.length;
      nextIndex = shuffledIndicesRef.current[nextShufflePos];
    } else {
      nextIndex = (currentTrackIndex + 1) % tracks.length;
    }

    const nextTrack = tracks[nextIndex];

    if (nextTrack) {
      // Preload URLs für den nächsten Track im Hintergrund (Cache)
      void preloadTrackUrls(nextTrack).catch(() => {
        // Fehler beim Preload sind ok
      });

      // Preload Audio-Element (falls noch nicht vorhanden)
      if (!nextTrackRef.current) {
        nextTrackRef.current = new Audio();
        nextTrackRef.current.preload = "auto";
        // Asynchron auflösen, ohne Render zu blockieren
        void (async () => {
          try {
            const url = await getQualityUrl(nextTrack, true); // Cache verwenden
            if (nextTrackRef.current) {
              nextTrackRef.current.src = url;
            }
          } catch (e) {
            // Ignoriere Preload-Fehler
          }
        })();
      }
    }
  }, [tracks, currentTrackIndex, getQualityUrl, preloadTrackUrls, shuffle]);

  useEffect(() => {
    if (!audioMediaWarmupAllowedRef.current) return;
    preloadNextTrack();
  }, [audioWarmupGeneration, preloadNextTrack]);

  // Initialize audio element (nur einmal; Cleanup nur beim Unmount)
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
      audioRef.current.loop = false;
      audioRef.current.preload = "none";

      // Connect to Web Audio API for volume control
      if (audioContextRef.current && gainNodeRef.current) {
        const source = audioContextRef.current.createMediaElementSource(
          audioRef.current
        );
        source.connect(gainNodeRef.current);
      }

      audioRef.current.addEventListener("loadstart", () => {
        setIsLoading(true);
      });

      audioRef.current.addEventListener("canplay", () => {
        setIsLoading(false);
      });
    }

    // Set volume (oder 0 wenn muted – volume bleibt erhalten)
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = muted ? 0 : volume;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  // Gain bei volume/muted ändern (ohne Audio-Element neu zu setzen oder zu zerstören)
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = muted ? 0 : volume;
    }
  }, [volume, muted]);

  // Separate useEffect for ended event listener to ensure it's always up to date
  useEffect(() => {
    if (!audioRef.current) return;

    const handleEnded = () => {
      debugBackgroundMusicLog(
        "🎵 [Background Music] Track ended - switching to next track"
      );
      nextTrack();
    };

    audioRef.current.addEventListener("ended", handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("ended", handleEnded);
      }
    };
  }, [nextTrack]);

  // Update current track
  useEffect(() => {
    if (tracks.length > 0 && currentTrackIndex < tracks.length) {
      const track = tracks[currentTrackIndex];
      setCurrentTrack(track);
      localStorage.setItem(BACKGROUND_MUSIC_START_TRACK_ID_KEY, track.id);
      setupMediaSession(track);
      if (audioMediaWarmupAllowedRef.current) {
        preloadNextTrack();
      }
    }
  }, [tracks, currentTrackIndex, setupMediaSession, preloadNextTrack]);

  // Aktualisiere Shuffle-Liste wenn sich Tracks oder Shuffle ändern
  useEffect(() => {
    if (tracks.length > 0) {
      initializeShuffle(tracks.length, currentTrackIndex);
    }
  }, [tracks.length, shuffle, initializeShuffle, currentTrackIndex]);

  // Wende Geschwindigkeitsanpassung an, wenn sich Modus, Ziel-BPM oder Track ändert
  useEffect(() => {
    applySpeedAdjustment();
  }, [applySpeedAdjustment]);

  // Audio-URL nur nach Karussell/Play-Warmup setzen (sonst voller Track-Download beim Mount).
  useEffect(() => {
    if (!audioMediaWarmupAllowedRef.current) return;
    if (!audioRef.current || !currentTrack || isPlaying) {
      // Nur vorbereiten wenn Musik NICHT läuft (sonst würde es stören)
      return;
    }

    // Asynchron URL auflösen und Audio-Element vorbereiten
    void (async () => {
      try {
        const isLocalSupabase =
          typeof import.meta.env.VITE_SUPABASE_URL === "string" &&
          import.meta.env.VITE_SUPABASE_URL.includes("127.0.0.1:54321");
        const qualityUrl = await getQualityUrl(
          currentTrack,
          true, // Cache verwenden
          isLocalSupabase
        );

        // Nur setzen wenn noch nicht gesetzt oder anders (vermeidet unnötige Reloads)
        if (audioRef.current && audioRef.current.src !== qualityUrl) {
          debugBackgroundMusicLog(
            "🎵 [Background Music] Audio-Element vorbereiten (Preload):",
            {
              track: currentTrack.title,
              url: qualityUrl,
            }
          );
          audioRef.current.src = qualityUrl;
          // Browser lädt automatisch die ersten Sekunden (preload="auto")
          // Musik startet NICHT automatisch - nur beim expliziten play()
        }
      } catch (error) {
        // Fehler beim Preload sind ok - wird beim tatsächlichen Abspielen nochmal versucht
        debugBackgroundMusicLog(
          "🎵 [Background Music] Preload-Fehler (wird beim Play ignoriert):",
          error
        );
      }
    })();
  }, [currentTrack, isPlaying, getQualityUrl, audioWarmupGeneration]);

  // Getter-Funktion für Audio-Element (für Video-Export mit Audio)
  const getAudioElement = useCallback(() => {
    return audioRef.current;
  }, []);

  // Wenn Musik aus: Software soll 106 BPM annehmen (z. B. für Video-Geschwindigkeit). Sonst gewählte Ziel-BPM.
  const effectiveTargetBpm = enabled ? (targetBpm ?? 106) : 106;

  const value: BackgroundMusicContextValue = {
    tracks,
    currentTrack,
    currentTrackIndex,
    isPlaying,
    volume,
    muted,
    quality,
    wifiOnly,
    enabled,
    isLoading,
    hasConsent,
    autoPlayOnMobile,
    wasManuallyTriggered,
    speedAdjustmentMode,
    targetBpm,
    shuffle,
    playbackRate,
    effectiveTargetBpm,
    giveConsent,
    play,
    playManually,
    playAuto,
    pause,
    nextTrack,
    previousTrack,
    playTrack,
    playTrackFromStart,
    seekTo,
    getCurrentTime,
    setVolume,
    setMuted,
    setQuality,
    setWifiOnly,
    setEnabled,
    setAutoPlayOnMobile,
    setPlaybackRate,
    setSpeedAdjustmentMode,
    setTargetBpm,
    setShuffle,
    tryAutoPlay,
    getAudioElement,
  };

  return (
    <BackgroundMusicContext.Provider value={value}>
      {children}
    </BackgroundMusicContext.Provider>
  );
}

export function useBackgroundMusic() {
  const context = useContext(BackgroundMusicContext);
  if (!context) {
    throw new Error(
      "useBackgroundMusic must be used within a BackgroundMusicProvider"
    );
  }
  return context;
}

// Optionaler Hook: Gibt null zurück, wenn kein Provider vorhanden ist
export function useBackgroundMusicOptional() {
  return useContext(BackgroundMusicContext);
}
